<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  accountName: {
    type: String,
    default: 'yourbrand',
  },
  accountAvatar: {
    type: String,
    default: '',
  },
  platform: {
    type: String,
    default: 'instagram', // 'instagram' or 'facebook'
  },
  dmMessage: {
    type: String,
    default: '',
  },
  publicReplyMessage: {
    type: String,
    default: '',
  },
  selectedPost: {
    type: Object,
    default: null,
  },
  postType: {
    type: String,
    default: 'all',
  },
});

const activeTab = ref('dm'); // 'post' | 'comments' | 'dm'

const formattedDm = computed(() => {
  if (!props.dmMessage) return 'Write a message';
  return props.dmMessage
    .replace(/\{\{\s*name\s*\}\}/gi, 'Alex')
    .replace(/\{\{\s*first_name\s*\}\}/gi, 'Alex')
    .replace(/\{\{\s*username\s*\}\}/gi, 'alex_user')
    .replace(/\{\{\s*commenter_name\s*\}\}/gi, 'Alex');
});

const formattedReply = computed(() => {
  if (!props.publicReplyMessage) return 'Hi @alex_user, check your DMs!';
  return props.publicReplyMessage
    .replace(/\{\{\s*name\s*\}\}/gi, 'Alex')
    .replace(/\{\{\s*first_name\s*\}\}/gi, 'Alex')
    .replace(/\{\{\s*username\s*\}\}/gi, 'alex_user')
    .replace(/\{\{\s*commenter_name\s*\}\}/gi, 'Alex');
});
</script>

<template>
  <div class="flex flex-col items-center select-none">
    <!-- Phone Mockup Frame -->
    <div
      class="relative w-[310px] h-[610px] bg-black rounded-[46px] p-[10px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.15)] border-[4px] border-[#2A2E37] overflow-hidden flex flex-col"
    >
      <!-- Phone Screen -->
      <div
        class="relative w-full h-full bg-[#0a0a0c] rounded-[36px] overflow-hidden flex flex-col text-white font-sans"
      >
        <!-- Status Bar -->
        <div
          class="flex items-center justify-between px-6 pt-3 pb-1 text-[11px] font-medium tracking-tight text-white/90 z-20 shrink-0"
        >
          <span>12:13</span>
          <!-- Dynamic Island / Speaker Pill -->
          <div class="w-20 h-4 bg-black rounded-full mx-auto" />
          <div class="flex items-center gap-1.5 text-white">
            <span class="i-lucide-signal text-[10px]" />
            <span class="i-lucide-wifi text-[10px]" />
            <span class="i-lucide-battery-full text-[12px]" />
          </div>
        </div>

        <!-- App Header -->
        <div
          class="flex items-center justify-between px-3.5 py-2 border-b border-white/5 bg-[#0a0a0c]/80 backdrop-blur shrink-0 z-10"
        >
          <div class="flex items-center gap-2.5 min-w-0">
            <span class="i-lucide-chevron-left text-lg text-white/80 shrink-0 cursor-pointer" />
            <div class="relative w-8 h-8 rounded-full bg-n-brand p-[1.5px] shrink-0">
              <div class="w-full h-full rounded-full bg-zinc-900 overflow-hidden flex items-center justify-center">
                <img
                  v-if="accountAvatar"
                  :src="accountAvatar"
                  :alt="accountName"
                  class="w-full h-full object-cover"
                />
                <span v-else class="text-xs font-bold text-white uppercase">
                  {{ accountName.slice(0, 2) }}
                </span>
              </div>
            </div>
            <div class="min-w-0 flex flex-col">
              <span class="text-xs font-semibold text-white truncate max-w-[120px]">
                {{ accountName }}
              </span>
              <span class="text-[9px] text-white/50 leading-tight">
                {{ platform === 'instagram' ? 'Instagram' : 'Facebook Page' }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-3 text-white/80 pr-1">
            <span class="i-lucide-phone text-sm" />
            <span class="i-lucide-video text-base" />
          </div>
        </div>

        <!-- Body Content -->
        <div class="flex-1 overflow-y-auto p-3 flex flex-col relative">
          <!-- DM VIEW -->
          <div v-if="activeTab === 'dm'" class="flex flex-col h-full justify-between">
            <div class="flex flex-col gap-3 pt-2">
              <div class="text-[10px] text-center text-white/40 my-1">Today</div>

              <!-- Message from user -->
              <div class="flex items-end gap-1.5 max-w-[80%] self-start">
                <div class="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] text-white shrink-0">
                  A
                </div>
                <div class="bg-[#262626] text-white text-[12px] px-3.5 py-2 rounded-2xl rounded-bl-sm leading-relaxed shadow-sm">
                  <span>Price please?</span>
                </div>
              </div>

              <!-- Automated Reply from Brand -->
              <div class="flex items-end gap-1.5 max-w-[85%] self-end">
                <div class="bg-n-brand text-white text-[12px] px-3.5 py-2.5 rounded-2xl rounded-br-sm leading-relaxed shadow-md break-words whitespace-pre-wrap">
                  {{ formattedDm }}
                </div>
              </div>
            </div>

            <!-- Bottom DM Input Bar -->
            <div class="pt-2">
              <div class="flex items-center gap-2 bg-[#1c1c1e] rounded-full px-3 py-2 border border-white/5">
                <div class="w-6 h-6 rounded-full bg-n-brand flex items-center justify-center shrink-0">
                  <span class="i-lucide-camera text-[11px] text-white" />
                </div>
                <span class="text-xs text-white/40 flex-1">Message...</span>
                <span class="i-lucide-mic text-xs text-white/50" />
                <span class="i-lucide-image text-xs text-white/50" />
              </div>
            </div>
          </div>

          <!-- COMMENTS VIEW -->
          <div v-else-if="activeTab === 'comments'" class="flex flex-col gap-3.5">
            <div class="text-xs font-semibold text-white/90 border-b border-white/10 pb-1.5">
              Comments
            </div>

            <!-- User Comment -->
            <div class="flex items-start gap-2.5 text-xs">
              <div class="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-white text-xs shrink-0">
                A
              </div>
              <div class="flex-1 flex flex-col">
                <div class="flex items-baseline gap-1.5">
                  <span class="font-semibold text-white">alex_user</span>
                  <span class="text-[10px] text-white/40">2m</span>
                </div>
                <p class="text-white/80 text-[11px] mt-0.5">
                  How much is this deal?
                </p>

                <!-- Brand Public Reply -->
                <div class="flex items-start gap-2 mt-2 pl-2 border-l-2 border-n-brand">
                  <div class="w-5 h-5 rounded-full bg-n-brand flex items-center justify-center text-[9px] text-white shrink-0">
                    {{ accountName.slice(0, 1) }}
                  </div>
                  <div class="flex-1 flex flex-col">
                    <div class="flex items-baseline gap-1.5">
                      <span class="font-semibold text-n-brand text-[11px]">{{ accountName }}</span>
                      <span class="text-[9px] text-white/40">Just now</span>
                    </div>
                    <p class="text-white/90 text-[11px] mt-0.5">
                      {{ formattedReply }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- POST VIEW -->
          <div v-else-if="activeTab === 'post'" class="flex flex-col gap-2">
            <div class="rounded-xl overflow-hidden bg-zinc-900 border border-white/10 aspect-square flex items-center justify-center relative">
              <img
                v-if="selectedPost?.thumbnail_url || selectedPost?.media_url"
                :src="selectedPost.thumbnail_url || selectedPost.media_url"
                alt="Selected post"
                class="w-full h-full object-cover"
              />
              <div v-else class="flex flex-col items-center justify-center text-center p-4 text-white/40">
                <span class="i-lucide-image text-3xl mb-2" />
                <span class="text-xs">
                  {{ postType === 'specific' ? 'No post selected' : 'Applies to any post / reel' }}
                </span>
              </div>
            </div>
            <div class="px-1 text-[11px] text-white/80 leading-snug line-clamp-3">
              <span class="font-bold text-white mr-1">{{ accountName }}</span>
              <span>{{ selectedPost?.caption || 'Check out our latest post! Drop a comment to get your discount.' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Phone Preview Tabs Under Phone -->
    <div class="flex items-center gap-1 mt-4 p-1 bg-n-surface-2 dark:bg-zinc-800 rounded-full border border-n-weak shadow-sm">
      <button
        type="button"
        class="px-4 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer"
        :class="
          activeTab === 'post'
            ? 'bg-n-brand text-white shadow-sm font-semibold'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = 'post'"
      >
        Post
      </button>
      <button
        type="button"
        class="px-4 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer"
        :class="
          activeTab === 'comments'
            ? 'bg-n-brand text-white shadow-sm font-semibold'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = 'comments'"
      >
        Comments
      </button>
      <button
        type="button"
        class="px-4 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer"
        :class="
          activeTab === 'dm'
            ? 'bg-n-brand text-white shadow-sm font-semibold'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = 'dm'"
      >
        DM
      </button>
    </div>
  </div>
</template>
